import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, ShoppingCart, MessageCircle, X, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';

const WHATSAPP_NUMBER = '551151250926';

// Categorias estáticas (pode ser expandido no futuro)
const categories = [
  { id: 'all', name: 'Todos' },
  { id: 'resinas', name: 'Resinas' },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

export const Products = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);
  const { addToCart } = useCart();
  
  // Buscar produtos da API
  const { products, loading, error } = useProducts();

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = 
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = 
        selectedCategory === 'all' || 
        product.category?.toLowerCase() === selectedCategory.toLowerCase();
      
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, selectedCategory]);

  const handleBuyClick = (product) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'add_to_cart', event_label: product.name });
    }
    
    // Adicionar ao carrinho com dados formatados
    addToCart({
      id: product.id,
      name: product.name,
      weight: product.weight,
      price: product.price,
      image: product.image,
      variantId: product.variantId || null,
      checkoutUrl: product.checkoutUrl
    });
    
    // Feedback visual
    setAddedProduct(product.id);
    setTimeout(() => setAddedProduct(null), 2000);
  };

  const handleWhatsAppClick = (product) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', event_label: `product_${product.name}` });
    }
    const message = `Olá, vim pelo site de vocês. Tenho interesse no produto: ${product.name} ${product.weight}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <main data-testid="products-page" className="min-h-screen bg-slate-50 pt-20">
      {/* Hero Banner */}
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              Loja Virtual
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white uppercase tracking-tight mb-4 leading-tight">
              Nossos Produtos
            </h1>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
              Resinas epóxi, tintas, primers e muito mais. <span className="text-white font-medium">Fabricação própria</span> com qualidade industrial.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-40">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Buscar produtos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-slate-50 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Category Filters - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  data-testid={`category-${category.id}`}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Mobile Filter Toggle */}
            <button
              data-testid="mobile-filter-btn"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-md text-slate-600"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mt-4 flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setShowFilters(false);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mb-4" />
              <p className="text-slate-600">Carregando produtos...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-16">
              <p className="text-red-500 mb-4">Erro ao carregar produtos</p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Tentar novamente
              </Button>
            </div>
          )}

          {/* Results Count */}
          {!loading && !error && (
            <div className="mb-8">
              <p className="text-slate-600">
                Mostrando <span className="font-semibold text-slate-900">{filteredProducts.length}</span> produto(s)
                {selectedCategory !== 'all' && (
                  <span> em <span className="font-semibold text-amber-600">{categories.find(c => c.id === selectedCategory)?.name}</span></span>
                )}
              </p>
            </div>
          )}

          {/* Products */}
          {!loading && !error && filteredProducts.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={fadeInUp}
                  data-testid={`product-${product.id}`}
                  className="bg-white rounded-xl overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200 group flex flex-col"
                >
                  {/* Product Image */}
                  <div className="relative h-48 overflow-hidden bg-slate-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {product.discount > 0 && (
                      <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-md">
                        {product.discount}% OFF
                      </span>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="mb-1">
                      <span className="text-xs text-amber-600 font-semibold uppercase tracking-wider">
                        {product.category}
                      </span>
                    </div>
                    <h3 className="font-heading text-base font-bold text-slate-900 mb-1 line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-slate-500 text-sm mb-2">{product.weight}</p>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {product.description}
                    </p>
                    
                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-slate-900">
                          R$ {product.price.toFixed(2).replace('.', ',')}
                        </span>
                        {product.originalPrice > product.price && (
                          <span className="text-sm text-slate-400 line-through">
                            R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions - sempre no fundo */}
                    <div className="flex gap-2 mt-auto">
                      <Button
                        data-testid={`buy-${product.id}`}
                        onClick={() => handleBuyClick(product)}
                        className={`flex-1 font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-all duration-300 ${
                          addedProduct === product.id
                            ? 'bg-green-500 hover:bg-green-500 text-white'
                            : 'bg-amber-500 hover:bg-amber-400 text-slate-900'
                        }`}
                      >
                        {addedProduct === product.id ? (
                          <>
                            <Check className="w-4 h-4" />
                            Adicionado!
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4" />
                            Adicionar
                          </>
                        )}
                      </Button>
                      <Button
                        data-testid={`whatsapp-${product.id}`}
                        onClick={() => handleWhatsAppClick(product)}
                        variant="outline"
                        className="px-3 border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-lg transition-colors duration-200"
                      >
                        <MessageCircle className="w-4 h-4 text-amber-500" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : !loading && !error && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                Nenhum produto encontrado
              </h3>
              <p className="text-slate-600 mb-6">
                Tente buscar com outros termos ou limpe os filtros.
              </p>
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                variant="outline"
                className="border-slate-300"
              >
                Limpar Filtros
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 md:py-16 bg-slate-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-2">
                Não encontrou o que procura?
              </h2>
              <p className="text-slate-400">
                Fale conosco pelo WhatsApp e descubra mais opções de produtos.
              </p>
            </div>
            <Button
              data-testid="products-page-whatsapp"
              onClick={() => {
                const message = 'Olá, vim pelo site de vocês. Gostaria de saber mais sobre os produtos disponíveis.';
                window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
              }}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 py-4 rounded-lg flex items-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg transition-all duration-200"
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Products;
